/* eslint-disable @typescript-eslint/no-explicit-any */
import retry from 'p-retry';
import { ResetPasswordErrorCode } from '@common/contracts';

import { useUserTokensStore } from '../../stores/userTokensStore/userTokensStore';

type LoginPayload =
  | {
      login: string;
      password: string;
    }
  | {
      accessCode: string;
      userId: string;
    };

interface UserGuest {
  userId: string;
  accessCode: string;
}

export class UserService {
  private static apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? 'api';

  public static async login(payload: LoginPayload): Promise<any> {
    const response = await fetch(`${this.apiBaseUrl}/users/login`, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const success = response.status === 200;

    const responseBody = await response.json();

    if (!success) {
      throw new Error('Invalid credentials.');
    }

    return responseBody;
  }

  public static async refreshToken(payload: any): Promise<any> {
    const { refreshToken } = payload;

    const response = await fetch(`${this.apiBaseUrl}/users/token`, {
      method: 'POST',
      body: JSON.stringify({
        refreshToken,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const success = response.status === 200;

    const responseBody = await response.json();

    if (!success) {
      const userStore = useUserTokensStore();

      userStore.removeUserTokens();

      throw new Error('Token expired. Login again.');
    }

    return responseBody;
  }

  public static async getMe(): Promise<any> {
    const userStore = useUserTokensStore();

    const response = await fetch(`${this.apiBaseUrl}/users/me`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userStore.getLoggedInUserAccessToken}`,
      },
    });

    const success = response.status >= 200 && response.status < 300;

    const responseBody = await response.json();

    if (!success) {
      const message = responseBody.error?.message ?? responseBody.message;

      await this.handleError(response.status, message);
    }

    return responseBody;
  }

  public static async createNewGuest(): Promise<UserGuest> {
    const userStore = useUserTokensStore();

    const res = await retry(async () => {
      const response = await fetch(`${this.apiBaseUrl}/guests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userStore.getLoggedInUserAccessToken}`,
        },
      });

      const success = response.status === 201;

      const responseBody = await response.json();

      if (!success) {
        const message = responseBody.error?.message ?? responseBody.message;

        await this.handleError(response.status, message);
      }

      return responseBody as UserGuest;
    });

    return res;
  }

  public static async register(payload: { email: string; password: string }): Promise<void> {
    const response = await fetch(`${this.apiBaseUrl}/users/register`, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const responseBody = await response.json();

    if (!response.ok) {
      throw new Error(responseBody.context?.reason || responseBody.message || 'Registration failed');
    }
  }

  public static async updateMarketingConsent(marketingConsent: boolean): Promise<void> {
    const userStore = useUserTokensStore();

    const response = await fetch(`${this.apiBaseUrl}/users/marketing-consent`, {
      method: 'PATCH',
      body: JSON.stringify({ marketingConsent }),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userStore.getLoggedInUserAccessToken}`,
      },
    });

    const success = response.status >= 200 && response.status < 300;
    const responseBody = await response.json();

    if (!success) {
      const message = responseBody.error?.message ?? responseBody.message;
      await this.handleError(response.status, message);
    }
  }

  public static async resetPassword(token: string, newPassword: string): Promise<void> {
    const response = await fetch(`${this.apiBaseUrl}/users/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        token,
        newPassword,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      if (response.status === 400) {
        if (data.code === ResetPasswordErrorCode.INVALID_TOKEN) {
          throw new Error('Link do resetowania hasła jest nieprawidłowy lub wygasł. Proszę zażądać nowego linku.');
        } else if (data.code === ResetPasswordErrorCode.INVALID_PASSWORD) {
          throw new Error(
            'Hasło nie spełnia wymagań bezpieczeństwa. Musi zawierać co najmniej 8 znaków, w tym wielkie i małe litery, cyfry oraz znaki specjalne.',
          );
        }
      }
      throw new Error(data.error || 'Wystąpił błąd podczas resetowania hasła.');
    }
  }

  public static async requestPasswordReset(login: string): Promise<void> {
    const response = await fetch(`${this.apiBaseUrl}/users/request-password-reset`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ login }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Wystąpił błąd podczas wysyłania prośby o reset hasła.');
    }
  }

  private static async handleError(status: number, message: string): Promise<void> {
    if (status === 401) {
      const userStore = useUserTokensStore();

      if (userStore.getLoggedInUserRefreshToken) {
        const response = await this.refreshToken({ refreshToken: userStore.getLoggedInUserRefreshToken });

        const { accessToken, expiresIn, refreshToken: newRefreshToken } = response;

        userStore.setUserTokens({
          accessToken,
          refreshToken: newRefreshToken,
          expiresIn,
        });

        throw new Error('Token expired. Retrying...');
      }
    } else {
      throw new Error(message);
    }
  }
}
